import { useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { coursesApi, type Lesson } from '@/api/courses';
import { usersApi, type MarathonEnrollmentRow } from '@/api/users';
import { GroupsPanel } from './groups/GroupsPanel';
import { progressApi } from '@/api/progress';
import { humanizeError } from '@/lib/errors';
import { keepPartsTogether } from '@/lib/keepPartsTogether';
import { marathonStudentCounts } from '@/lib/marathonStudentCount';
import { Card, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/Pill';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Avatar } from '@/components/ui/Avatar';
import { MarathonFormModal } from './MarathonFormModal';
import { MarathonSettingsTab } from './MarathonSettingsTab';
import { AccessCopyButton } from '@/components/admin/AccessCopyButton';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { useAuthStore } from '@/stores/auth';
import { StudentRowMenu, type RowNotice } from '@/components/admin/StudentRowMenu';
import { canAssignCurator, enrollmentStatusKey, filterEnrollmentRows, lifecycleErrorText } from '@/lib/enrollmentActions';
import { matchesSearch } from '@/lib/search';
import { SearchInput } from '@/components/ui/SearchInput';
import type { TransferOp } from '@/lib/contentTransfer';
import { canStepLesson, newLessonModuleId, stepErrorText } from '@/lib/lessonListOrder';
import { TransferDialog, TransferMenu } from './LessonBuilder/TransferDialog';
import { ActionSheet } from '@/components/mobile/ActionSheet';
import { MAdd, MBlock, MChips, MEmpty, MTag, MTitle } from '@/components/mobile/MobileKit';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';

type LessonDraft = { id: string; title: string; points: number; status: 'draft' | 'published' };

/** Учётка заведена, а запись на курс отказала (техдолг п. 169). `reason` — причина словами, если есть. */
class EnrollAfterCreateError extends Error {
  reason: string;
  constructor(reason: string) {
    super('enroll failed after the account was created');
    this.reason = reason;
  }
}

// Label maps are built inside the component using t().

/** Вкладки карточки курса — один порядок на компьютер и телефон. */
const COURSE_TABS = [
  ['students', 'adminMarathonDetail.tabStudents'],
  ['settings', 'adminMarathonDetail.tabSettings'],
  ['content', 'adminMarathonDetail.tabLesson'],
  ['curators', 'adminMarathonDetail.tabCuratorsShort'],
  ['stats', 'adminMarathonDetail.tabStats'],
] as const;

export function AdminMarathonDetailPage() {
  const ask = useConfirm();
  const { t } = useTranslation();
  const { marathonId } = useParams<{ marathonId: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const STATUS_LABELS: Record<string, string> = {
    draft: t('marathon.statusDraft'),
    published: t('marathon.statusPublished'),
    archived: t('marathon.statusArchived'),
  };
  const statusLabel = (s?: string) => STATUS_LABELS[(s ?? '').toLowerCase()] ?? s ?? '—';
  const lessonStatusLabel = (s?: string) => (s === 'published' ? t('marathon.statusPublished') : t('marathon.statusDraft'));

  const [tab, setTab] = useState<'students' | 'content' | 'curators' | 'stats' | 'settings'>('content');
  const [lessonSearch, setLessonSearch] = useState('');
  const [headerMenu, setHeaderMenu] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [lessonEdit, setLessonEdit] = useState<(LessonDraft & { module_id: string }) | null>(null);
  const [addLessonForModule, setAddLessonForModule] = useState<string | null>(null);
  const [newLessonTitle, setNewLessonTitle] = useState('');
  const [newLessonPoints, setNewLessonPoints] = useState(10);
  // «Копировать в…» / «Переместить в…» для урока (договор constructor-v2.md §2).
  const [lessonTransfer, setLessonTransfer] = useState<{ id: string; op: TransferOp } | null>(null);
  // Телефон (§5.3): лист «⋯» шапки курса и лист «⋯» урока.
  const isMobile = useIsMobile();
  const [headSheet, setHeadSheet] = useState(false);
  const [lessonSheet, setLessonSheet] = useState<{ lesson: Lesson; moduleId: string; index: number } | null>(null);

  const marathon = useQuery({
    queryKey: ['admin', 'marathon', marathonId],
    queryFn: () => coursesApi.getMarathon(marathonId!),
    enabled: !!marathonId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // ⚠️ Число учеников — из того же запроса, что и вкладка «Ученики» (общий
  // ключ, один поход на сервер). `student_count` карточки марафона сервер не
  // заполняет: в шапке стоял 0 при 23 в списке (10.09.2026).
  const students = useQuery({
    queryKey: ['admin', 'marathon-students', marathonId],
    queryFn: () => usersApi.listMarathonEnrollments(marathonId!),
    enabled: !!marathonId,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'marathon', marathonId] });

  const addLesson = useMutation({
    mutationFn: (moduleId: string) =>
      coursesApi.createLesson({
        module_id: moduleId,
        title: newLessonTitle.trim(),
        points: newLessonPoints,
      }),
    onSuccess: () => {
      setAddLessonForModule(null);
      setNewLessonTitle('');
      setNewLessonPoints(10);
      invalidate();
    },
  });

  const saveLesson = useMutation({
    mutationFn: (d: LessonDraft) =>
      coursesApi.updateLesson(d.id, { title: d.title.trim(), points: d.points, status: d.status }),
    onSuccess: () => { setLessonEdit(null); invalidate(); },
  });

  const removeLesson = useMutation({
    mutationFn: (id: string) => coursesApi.deleteLesson(id),
    onSuccess: invalidate,
  });

  // Стрелки «Выше»/«Ниже»: шаг по всему плоскому списку, через границу модулей
  // тоже — место считает сервер (constructor-v2.md §2.8). Раньше страница меняла
  // уроки местами внутри модуля, и у первого урока модуля «Выше» не делало ничего.
  const stepLesson = useMutation({
    mutationFn: ({ id, dir }: { id: string; dir: -1 | 1 }) =>
      coursesApi.stepLesson(id, dir < 0 ? 'up' : 'down'),
    onSuccess: invalidate,
  });

  const publish = useMutation({
    mutationFn: () => coursesApi.publishMarathon(marathonId!),
    onSuccess: invalidate,
  });
  const archive = useMutation({
    mutationFn: () => coursesApi.archiveMarathon(marathonId!),
    onSuccess: invalidate,
  });
  const deleteMarathon = useMutation({
    mutationFn: () => coursesApi.deleteMarathon(marathonId!),
    onSuccess: () => navigate('/admin/marathons'),
  });

  if (marathon.isLoading) return <Card variant="subtle">{t('common.loading')}</Card>;
  if (marathon.error) {
    return (
      <Card variant="subtle" className="text-error">
        {humanizeError(marathon.error)}{' '}
        <button type="button" onClick={() => navigate('/admin/marathons')} className="font-semibold underline">
          {t('adminMarathonDetail.backToList')}
        </button>
      </Card>
    );
  }
  if (!marathon.data) {
    return (
      <Card variant="subtle" className="text-center text-on-surface-variant">
        {t('adminMarathonDetail.notFound')}{' '}
        <button type="button" onClick={() => navigate('/admin/marathons')} className="font-semibold text-primary">
          {t('adminMarathonDetail.backToList')}
        </button>
      </Card>
    );
  }

  const m = marathon.data;

  // Любая из операций над содержимым/марафоном может упасть — показываем причину.
  const actionError =
    addLesson.error || saveLesson.error || removeLesson.error ||
    publish.error || archive.error || deleteMarathon.error;

  // Курс — ОДИН плоский список уроков, модули скрыты. Место урока в этом списке
  // (index) — то, по чему стрелка решает, есть ли куда шагнуть; поиск его не меняет.
  const flatLessons = (m.modules ?? []).flatMap((mod) =>
    (mod.lessons ?? []).map((lesson) => ({ lesson, mod })),
  ).map((row, index) => ({ ...row, index }));
  const shownLessons = flatLessons.filter(({ lesson }) =>
    lesson.title.toLowerCase().includes(lessonSearch.trim().toLowerCase()),
  );
  // «+ Добавить урок»: урок кладётся в модуль последнего урока списка, чтобы
  // встать снизу; у нового курса модуль заводится на лету.
  const onAddLesson = async () => {
    let modId = newLessonModuleId(m.modules);
    if (!modId) {
      const created = await coursesApi.createModule({ marathon_id: marathonId!, title: t('adminMarathonDetail.modulesTitle') });
      modId = created.id;
      await invalidate();
    }
    setAddLessonForModule(modId);
  };
  // Ученики — с действующим доступом, как в списке курсов; истёкшие — отдельно и только если есть (техдолг 416).
  const studentCounts = marathonStudentCounts(students.data);
  const studentCount = studentCounts?.active ?? null;
  const expiredLabel =
    studentCounts && studentCounts.expired > 0
      ? t('adminMarathonDetail.expiredN', { count: studentCounts.expired })
      : null;
  const lessonCount = flatLessons.length;
  // Подтверждения — одни на компьютер и телефон.
  const askDeleteMarathon = async () => {
    if (await ask({ message: t('adminMarathonDetail.deleteConfirm', { title: m.title }), danger: true })) deleteMarathon.mutate();
  };
  const askDeleteLesson = async (l: Lesson) => {
    if (await ask({ message: t('adminMarathonDetail.deleteLessonConfirm', { title: l.title }), danger: true })) removeLesson.mutate(l.id);
  };
  // Кнопки окон урока: на телефоне — внизу окна во весь экран (правило 3), на компьютере — под полями, как было.
  const newLessonButtons = (
    <div className={isMobile ? 'grid grid-cols-2 gap-2' : 'flex justify-end gap-2'}>
      <Button variant="ghost" onClick={() => setAddLessonForModule(null)}>{t('common.cancel')}</Button>
      <Button
        disabled={!newLessonTitle.trim()}
        loading={addLesson.isPending}
        onClick={() => addLessonForModule && addLesson.mutate(addLessonForModule)}
      >
        {t('adminMarathonDetail.create')}
      </Button>
    </div>
  );
  const lessonEditButtons = (d: LessonDraft) => (
    <div className={isMobile ? 'grid grid-cols-2 gap-2' : 'flex justify-end gap-2'}>
      <Button variant="ghost" onClick={() => setLessonEdit(null)}>{t('common.cancel')}</Button>
      <Button disabled={!d.title.trim()} loading={saveLesson.isPending} onClick={() => saveLesson.mutate(d)}>
        {t('common.save')}
      </Button>
    </div>
  );
  const lessonSheetActions = (s: { lesson: Lesson; moduleId: string; index: number }) => {
    const l = s.lesson;
    return [
      // «Конструктор», а не второе «Редактировать»: в листе две строки различались только значком.
      { label: t('adminMarathons.constructor'), icon: 'edit', onSelect: () => navigate(`/admin/lessons/${l.id}/builder`) },
      {
        label: t('common.edit'),
        icon: 'tune',
        onSelect: () => setLessonEdit({ id: l.id, module_id: s.moduleId, title: l.title, points: l.points, status: l.status }),
      },
      ...(canStepLesson(s.index, flatLessons.length, -1)
        ? [{ label: t('adminMarathonDetail.moveUp'), icon: 'expand_less', onSelect: () => stepLesson.mutate({ id: l.id, dir: -1 }) }]
        : []),
      ...(canStepLesson(s.index, flatLessons.length, 1)
        ? [{ label: t('adminMarathonDetail.moveDown'), icon: 'expand_more', onSelect: () => stepLesson.mutate({ id: l.id, dir: 1 }) }]
        : []),
      { label: t('lessonBuilder.copyTo'), icon: 'content_copy', onSelect: () => setLessonTransfer({ id: l.id, op: 'copy' }) },
      { label: t('lessonBuilder.moveTo'), icon: 'arrow_forward', onSelect: () => setLessonTransfer({ id: l.id, op: 'move' }) },
      { label: t('common.delete'), icon: 'delete', danger: true, onSelect: () => void askDeleteLesson(l) },
    ];
  };

  return (
    <>
      {isMobile ? (
        // Телефон (§5.3): шапка набора «назад · название · учеников · уроков», справа «Настройки» и «⋯» — тот же
        // состав, что в меню компьютера; метки «Открытый / Закрытый» и статус строкой под шапкой.
        <div className="mb-3 space-y-2" data-course-head-phone>
          <MTitle
            back="/admin/marathons"
            title={m.title}
            /* Перенос — только между частями: кадр 390 17.10 рвал «5 | уроков» (keepPartsTogether). */
            subtitle={keepPartsTogether(`${
              studentCount == null
                ? `— ${t('adminMarathons.students')}`
                : t('curatorStudents.studentCount', { count: studentCount })
            }${expiredLabel ? ` · ${expiredLabel}` : ''} · ${t('staffMobile.lessonsN', { count: lessonCount })}`)}
            right={
              <div className="flex flex-none gap-1.5">
                <button
                  type="button"
                  onClick={() => setEditOpen(true)}
                  aria-label={t('adminMarathonDetail.settings')}
                  className="grid h-11 w-11 place-items-center rounded-full bg-surface-container-low text-on-surface"
                >
                  <span className="material-symbols-outlined text-[20px]">settings</span>
                </button>
                <button
                  type="button"
                  onClick={() => setHeadSheet(true)}
                  aria-label={t('nav.more')}
                  className="grid h-11 w-11 place-items-center rounded-full bg-surface-container-low text-on-surface"
                >
                  <span className="material-symbols-outlined text-[20px]">more_horiz</span>
                </button>
              </div>
            }
          />
          <div className="flex flex-wrap gap-1.5 px-0.5">
            <MTag tone={m.access_mode === 'open' ? 'info' : 'muted'}>
              {m.access_mode === 'open' ? t('adminMarathonDetail.openBadge') : t('adminMarathonDetail.closedBadge')}
            </MTag>
            <MTag tone={m.status === 'published' ? 'info' : m.status === 'archived' ? 'muted' : 'warm'}>{statusLabel(m.status)}</MTag>
          </div>
          <ActionSheet
            open={headSheet}
            onClose={() => setHeadSheet(false)}
            title={m.title}
            actions={[
              ...(m.status !== 'published'
                ? [{ label: t('adminMarathons.publish'), icon: 'rocket_launch', onSelect: () => publish.mutate() }]
                : []),
              ...(m.status === 'published'
                ? [{ label: t('adminMarathons.toArchive'), icon: 'inventory_2', onSelect: () => archive.mutate() }]
                : []),
              { label: t('common.delete'), icon: 'delete', danger: true, onSelect: () => void askDeleteMarathon() },
            ]}
          />
        </div>
      ) : (
      <>
      {/* Course header — Edvibe-style: cover + title + counts/status + actions */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="h-24 w-24 shrink-0 overflow-hidden rounded-card bg-surface-container-high">
          {m.cover_url ? (
            <img src={m.cover_url} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-on-surface-variant">
              <span className="material-symbols-outlined text-3xl">school</span>
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-3xl font-bold tracking-display text-on-surface">{m.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            <span className="inline-flex items-center gap-1 text-on-surface-variant">
              <span className="material-symbols-outlined text-base text-tertiary">group</span>
              {studentCount ?? '—'}
              {expiredLabel && <span data-expired-students>· {expiredLabel}</span>}
            </span>
            <span className="inline-flex items-center gap-1 text-on-surface-variant">
              <span className="material-symbols-outlined text-base text-primary">menu_book</span>
              {lessonCount}
            </span>
            <Pill variant={m.access_mode === 'open' ? 'success' : 'neutral'}>
              {m.access_mode === 'open' ? t('adminMarathonDetail.openBadge') : t('adminMarathonDetail.closedBadge')}
            </Pill>
            <Pill variant={m.status === 'published' ? 'success' : m.status === 'archived' ? 'neutral' : 'warning'}>{statusLabel(m.status)}</Pill>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
              <span className="material-symbols-outlined">settings</span>
              {t('adminMarathonDetail.settings')}
            </Button>
            <div className="relative">
              <Button variant="ghost" size="sm" onClick={() => setHeaderMenu((o) => !o)} title="…">
                <span className="material-symbols-outlined">more_horiz</span>
              </Button>
              {headerMenu && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setHeaderMenu(false)} />
                  <div className="absolute left-0 z-30 mt-1 w-52 rounded-card bg-surface-container-lowest p-1 shadow-ambient">
                    {m.status !== 'published' && (
                      <MenuItem icon="rocket_launch" label={t('adminMarathons.publish')} onClick={() => { setHeaderMenu(false); publish.mutate(); }} />
                    )}
                    {m.status === 'published' && (
                      <MenuItem icon="inventory_2" label={t('adminMarathons.toArchive')} onClick={() => { setHeaderMenu(false); archive.mutate(); }} />
                    )}
                    <MenuItem icon="delete" danger label={t('common.delete')} onClick={() => { setHeaderMenu(false); void askDeleteMarathon(); }} />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      </>
      )}

      {(stepLesson.error || actionError) && (
        <Card variant="subtle" className="mb-4 text-sm text-error">
          {stepLesson.error ? stepErrorText(stepLesson.error, t) : humanizeError(actionError)}
        </Card>
      )}

      {isMobile ? (
        // Телефон: вкладки фишками в одну строку с прокруткой — пять подписей в MSegmented на 390 не помещаются.
        <div className="mb-3" data-course-tabs-phone>
          <MChips
            chips={COURSE_TABS.map(([key, labelKey]) => ({ key, label: t(labelKey) }))}
            active={tab}
            onChange={setTab}
          />
        </div>
      ) : (
      <div className="mb-6 flex gap-1 overflow-x-auto border-b border-outline">
        {COURSE_TABS.map(([key, labelKey]) => [key, t(labelKey)] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`-mb-px whitespace-nowrap border-b-2 px-4 py-2 text-sm font-semibold transition ${
              tab === key ? 'border-primary text-primary' : 'border-transparent text-on-surface-variant'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      )}

      {tab === 'students' && <StudentsTab marathonId={marathonId!} />}

      {tab === 'stats' && (
        <StatsTab
          marathonId={marathonId!}
          studentCount={studentCount}
          lessonCount={lessonCount}
          publishedCount={flatLessons.filter((x) => x.lesson.status === 'published').length}
        />
      )}

      {tab === 'curators' && <CuratorsTab marathonId={marathonId!} marathonStatus={m.status} />}

      {/* ⚠️ ВКЛАДКА «ТАРИФЫ» УБРАНА В ТЕНЬ (07.09.2026). Права и состав
          дополнительных курсов переехали в «Настройки», сами тарифы остались в
          базе нетронутыми — их просто больше не настраивают отсюда. */}
      {tab === 'settings' && <MarathonSettingsTab marathonId={marathonId!} />}

      {tab === 'content' && isMobile && (
        // Телефон (§5.3 «Урок»): строка урока — название целиком (две строки), «очков · статус»; пять значков 28 px
        // справа (стрелки, «⋮», настройки, корзина) → один «⋯» 44 px с подписанным листом. Нажатие — конструктор.
        <div className="space-y-3" data-course-lessons-phone>
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <SearchInput
                placeholder={t('adminMarathonDetail.lessonSearch')}
                value={lessonSearch}
                onSearch={setLessonSearch}
              />
            </div>
            <MAdd label={t('adminMarathonDetail.addLesson')} onClick={onAddLesson} />
          </div>
          <p className="px-0.5 text-[12px] text-on-surface-variant">
            {t('adminMarathonDetail.lessonCountLabel', { count: lessonCount })}
          </p>
          {shownLessons.length === 0 ? (
            <MEmpty icon="menu_book" title={t('adminMarathonDetail.noLessons')} />
          ) : (
            <MBlock>
              {shownLessons.map(({ lesson: l, mod, index }, i) => (
                <div
                  key={l.id}
                  data-course-lesson-row
                  className={cn('flex items-center gap-2 py-3', i < shownLessons.length - 1 && 'border-b border-outline')}
                >
                  <Link to={`/admin/lessons/${l.id}/builder`} className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-[14px] font-semibold leading-[1.3] text-on-surface">{l.title}</span>
                    <span className="block truncate text-[11.5px] text-on-surface-variant">
                      {t('adminMarathonDetail.points', { count: l.points })} · {lessonStatusLabel(l.status)}
                    </span>
                  </Link>
                  <button
                    type="button"
                    onClick={() => setLessonSheet({ lesson: l, moduleId: mod.id, index })}
                    aria-label={t('nav.more')}
                    className="grid h-11 w-11 flex-none place-items-center rounded-full text-on-surface-variant"
                  >
                    <span className="material-symbols-outlined text-[20px]">more_horiz</span>
                  </button>
                </div>
              ))}
            </MBlock>
          )}
          <ActionSheet
            open={!!lessonSheet}
            onClose={() => setLessonSheet(null)}
            title={lessonSheet?.lesson.title}
            actions={lessonSheet ? lessonSheetActions(lessonSheet) : []}
          />
        </div>
      )}

      {tab === 'content' && !isMobile && (
        <div>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant">search</span>
              <Input
                className="pl-10"
                placeholder={t('adminMarathonDetail.lessonSearch')}
                value={lessonSearch}
                onChange={(e) => setLessonSearch(e.target.value)}
              />
            </div>
            <Button onClick={onAddLesson}>
              <span className="material-symbols-outlined">add</span>
              {t('adminMarathonDetail.addLesson')}
            </Button>
          </div>

          <p className="mb-3 text-xs font-semibold uppercase tracking-label text-on-surface-variant">
            {t('adminMarathonDetail.lessonCountLabel', { count: lessonCount })}
          </p>

          {shownLessons.length === 0 ? (
            <Card variant="subtle" className="text-center text-on-surface-variant">
              {t('adminMarathonDetail.noLessons')}
            </Card>
          ) : (
            <ul className="space-y-2">
              {shownLessons.map(({ lesson: l, mod, index }) => (
                <li key={l.id}>
                  <Card variant="subtle" className="flex items-center gap-3">
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-button bg-surface-container-high">
                      {l.cover_url ? (
                        <img src={l.cover_url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-on-surface-variant">
                          <span className="material-symbols-outlined">menu_book</span>
                        </div>
                      )}
                    </div>
                    <Link to={`/admin/lessons/${l.id}/builder`} className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-on-surface">{l.title}</span>
                      {/* flex-wrap: на 390 плашки не влезают в строку рядом с кнопками и
                          наезжали на «⋮» и настройки урока — переносим их вниз. */}
                      <span className="mt-1 flex flex-wrap items-center gap-1.5">
                        <Pill variant="neutral">{t('adminMarathonDetail.points', { count: l.points })}</Pill>
                        <Pill variant={l.status === 'published' ? 'success' : 'warning'}>{lessonStatusLabel(l.status)}</Pill>
                      </span>
                    </Link>
                    <div className="flex flex-col">
                      <button type="button" className="text-on-surface-variant hover:text-on-surface disabled:opacity-30 disabled:hover:text-on-surface-variant" disabled={!canStepLesson(index, flatLessons.length, -1) || stepLesson.isPending} onClick={() => stepLesson.mutate({ id: l.id, dir: -1 })} title={t('adminMarathonDetail.moveUp')}>
                        <span className="material-symbols-outlined text-base">expand_less</span>
                      </button>
                      <button type="button" className="text-on-surface-variant hover:text-on-surface disabled:opacity-30 disabled:hover:text-on-surface-variant" disabled={!canStepLesson(index, flatLessons.length, 1) || stepLesson.isPending} onClick={() => stepLesson.mutate({ id: l.id, dir: 1 })} title={t('adminMarathonDetail.moveDown')}>
                        <span className="material-symbols-outlined text-base">expand_more</span>
                      </button>
                    </div>
                    <Link to={`/admin/lessons/${l.id}/builder`} className="hidden sm:block">
                      <Button variant="secondary" size="sm">
                        <span className="material-symbols-outlined">edit</span>
                        {t('adminMarathonDetail.editBtn')}
                      </Button>
                    </Link>
                    <TransferMenu onPick={(op) => setLessonTransfer({ id: l.id, op })} />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setLessonEdit({ id: l.id, module_id: mod.id, title: l.title, points: l.points, status: l.status })}
                      title={t('common.edit')}
                    >
                      <span className="material-symbols-outlined">tune</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void askDeleteLesson(l)}
                      title={t('common.delete')}
                    >
                      <span className="material-symbols-outlined text-error">delete</span>
                    </Button>
                  </Card>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <MarathonFormModal
        // Remount per marathon so SPA-navigating between two marathons (same
        // /admin/marathons/:id route, no page remount) reinitialises the form
        // from the current marathon — otherwise the previous marathon's cached
        // defaultValues (title/slug/is_private) would silently overwrite it.
        key={m.id}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        initial={m}
        onCreated={() => qc.invalidateQueries({ queryKey: ['admin', 'marathon'] })}
      />

      <Modal
        open={!!addLessonForModule}
        onClose={() => setAddLessonForModule(null)}
        title={t('adminMarathonDetail.newLessonTitle')}
        phoneFull
        footer={isMobile ? newLessonButtons : undefined}
      >
        <div className="space-y-3">
          <Input
            placeholder={t('adminMarathonDetail.lessonTitlePlaceholder')}
            autoFocus
            value={newLessonTitle}
            onChange={(e) => setNewLessonTitle(e.target.value)}
          />
          <label className="block text-sm">
            {t('adminMarathonDetail.lessonPoints')}
            <Input
              type="number"
              min={0}
              value={newLessonPoints}
              onChange={(e) => setNewLessonPoints(Number(e.target.value))}
            />
          </label>
          {!isMobile && newLessonButtons}
        </div>
      </Modal>

      <Modal
        open={!!lessonEdit}
        onClose={() => setLessonEdit(null)}
        title={t('adminMarathonDetail.editLessonTitle')}
        phoneFull
        footer={isMobile && lessonEdit ? lessonEditButtons(lessonEdit) : undefined}
      >
        {lessonEdit && (
          <LessonEditForm
            draft={lessonEdit}
            onChange={(next) => setLessonEdit({ ...lessonEdit, ...next })}
            buttons={isMobile ? null : lessonEditButtons(lessonEdit)}
          />
        )}
      </Modal>

      {lessonTransfer && (
        <TransferDialog
          kind="lesson"
          op={lessonTransfer.op}
          sourceId={lessonTransfer.id}
          source={{ marathonId, isTemplate: m.is_template === true }}
          // Префикс ['admin','marathon'] задевает и этот курс, и курс назначения.
          onDone={() => qc.invalidateQueries({ queryKey: ['admin', 'marathon'] })}
          onClose={() => setLessonTransfer(null)}
        />
      )}
    </>
  );
}

function MenuItem({ icon, label, onClick, danger }: { icon: string; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-button px-3 py-2 text-left text-sm ${
        danger ? 'text-error hover:bg-error-container/50' : 'text-on-surface hover:bg-surface-container-high'
      }`}
    >
      <span className="material-symbols-outlined text-base">{icon}</span>
      {label}
    </button>
  );
}

function StatCard({ icon, label, value }: { icon: string; label: string; value: number | string }) {
  return (
    <Card variant="subtle" className="flex flex-col items-center gap-1 text-center">
      <span className="material-symbols-outlined text-2xl text-primary">{icon}</span>
      <span className="font-display text-2xl font-bold tracking-display text-on-surface">{value}</span>
      <span className="text-xs text-on-surface-variant">{label}</span>
    </Card>
  );
}

export function StudentsTab({ marathonId }: { marathonId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const [adding, setAdding] = useState(false);
  // Итог действия из меню «⋯» — над списком: тостов в проекте нет.
  const [notice, setNotice] = useState<RowNotice | null>(null);
  // Поиск по уже загруженному списку (имя, почта) — отдельно от поиска в «Добавить учеников».
  const [rowQuery, setRowQuery] = useState('');
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [addError, setAddError] = useState('');

  const q = useQuery({
    queryKey: ['admin', 'marathon-students', marathonId],
    queryFn: () => usersApi.listMarathonEnrollments(marathonId),
  });
  const rows: MarathonEnrollmentRow[] = q.data?.items ?? [];

  // Кого можно добавить: ученики, которых на курсе ещё нет.
  const candidates = useQuery({
    queryKey: ['admin', 'students-for-enroll', search],
    queryFn: () => usersApi.list({ role: 'student', q: search || undefined, limit: 50 }),
    enabled: adding,
  });
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newNote, setNewNote] = useState('');
  // Кого только что завели: администратору сразу нужна кнопка с доступом,
  // иначе он идёт искать этого человека в списке — а список длинный.
  const [justAdded, setJustAdded] = useState<{ id: string; email: string } | null>(null);
  const alreadyIn = new Set(rows.map((r) => r.student_id));
  const free = (candidates.data?.items ?? []).filter((u) => !alreadyIn.has(u.id));

  /**
   * ⚠️ ЗАПИСЫВАЕМ БЕЗ ТАРИФА (переезд 07.09.2026).
   *
   * Права ученик получает от самого курса, дополнительные курсы выдаются по
   * его настройкам. Раньше добавить ученика можно было только через тариф —
   * а после того как тарифы ушли в тень, добавлять стало нечем вовсе.
   */
  const add = useMutation({
    mutationFn: () =>
      usersApi.bulkEnroll({ marathon_id: marathonId, student_ids: [...chosen] }),
    onSuccess: () => {
      setAddError('');
      setChosen(new Set());
      setAdding(false);
      qc.invalidateQueries({ queryKey: ['admin', 'marathon-students', marathonId] });
    },
    onError: (e) => setAddError(humanizeError(e)),
  });

  /**
   * Завести ученика прямо здесь.
   *
   * ⚠️ ЗАЧЕМ ОТДЕЛЬНО ОТ СПИСКА. В списке только те, кто уже заведён на
   * платформе. Нового человека приходилось создавать в другом разделе, потом
   * возвращаться сюда и искать его в поиске — заказчик 07.09.2026 спросил,
   * почему нельзя сразу.
   *
   * ⚠️ АДРЕС МОЖЕТ БЫТЬ УЖЕ ЗАНЯТ, и это не поломка: половина списка обычно
   * уже учится. Сервер возвращает отказ вместе с тем, кто занимает адрес, —
   * записываем его на курс вместо того, чтобы показать «конфликт» и оставить
   * человека гадать.
   *
   * Письмо с доступом (адрес, пароль, ссылка) отправляет сервер сам.
   */
  const createNew = useMutation({
    mutationFn: async () => {
      let id: string;
      const email = newEmail.trim();
      try {
        const res = await usersApi.createStudent({
          full_name: newName.trim(),
          email,
        });
        id = res.user.id;
        setNewNote(t('adminMarathonDetail.newStudentDone'));
      } catch (e) {
        const raw = (e as { raw?: { existing?: { id?: string } } })?.raw;
        if (!raw?.existing?.id) throw e;
        id = raw.existing.id;
        setNewNote(t('adminMarathonDetail.newStudentExisted'));
      }
      // ⚠️ ОТКАЗ ЗАПИСИ ПОСЛЕ ЗАВЕДЕНИЯ — СВОИМИ СЛОВАМИ (техдолг п. 169, 12.09.2026).
      // `bulkEnroll` на 207 не бросает, а отдаёт `failed`, — и отказ записи
      // показывался как «Ученик заведён». Учётка уже есть, поэтому повтор той же
      // кнопкой получит 409 с этим же учеником и запишет его: двойника не будет.
      let enrolled: Awaited<ReturnType<typeof usersApi.bulkEnroll>>;
      try {
        enrolled = await usersApi.bulkEnroll({ marathon_id: marathonId, student_ids: [id] });
      } catch (e) {
        throw new EnrollAfterCreateError(humanizeError(e));
      }
      if (enrolled.failed > 0) throw new EnrollAfterCreateError('');
      setJustAdded({ id, email });
    },
    onSuccess: () => {
      setAddError('');
      setNewName('');
      setNewEmail('');
      qc.invalidateQueries({ queryKey: ['admin', 'marathon-students', marathonId] });
    },
    onError: (e) => {
      setNewNote('');
      setJustAdded(null);
      setAddError(
        e instanceof EnrollAfterCreateError
          ? [t('adminMarathonDetail.newStudentEnrollFailed'), e.reason].filter(Boolean).join(' ')
          : humanizeError(e),
      );
    },
  });

  const pick = (id: string) => {
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (q.isLoading) return <Card variant="subtle">{t('common.loading')}</Card>;
  if (q.isError) return <Card variant="subtle" className="text-error">{humanizeError(q.error)}</Card>;

  const addBlock = (
    <>
      {!adding ? (
        <Button className="mb-3" onClick={() => setAdding(true)}>
          {t('adminMarathonDetail.addStudents')}
        </Button>
      ) : (
        <Card className="mb-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <CardTitle>{t('adminMarathonDetail.addStudents')}</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => { setAdding(false); setChosen(new Set()); }}>
              {t('common.cancel')}
            </Button>
          </div>
          {addError && <p className="mb-2 text-sm text-error">{addError}</p>}
          {/* Кандидаты ищутся на сервере — с задержкой, а не запросом на каждую букву (28.09.2026). */}
          <SearchInput placeholder={t('adminMarathonDetail.searchStudent')} value={search} onSearch={setSearch} />
          <div className="mt-3 max-h-[320px] space-y-1.5 overflow-y-auto">
            {candidates.isLoading && <p className="text-sm text-on-surface-variant">{t('common.loading')}</p>}
            {!candidates.isLoading && free.length === 0 && (
              <p className="text-sm text-on-surface-variant">{t('adminMarathonDetail.noCandidates')}</p>
            )}
            {free.map((u) => (
              <label
                key={u.id}
                className="flex cursor-pointer items-center gap-2.5 rounded-button bg-surface-container-low px-3 py-2"
              >
                <input
                  type="checkbox"
                  checked={chosen.has(u.id)}
                  onChange={() => pick(u.id)}
                  className="h-4 w-4 accent-primary"
                />
                <span className="min-w-0 flex-1 truncate text-[13.5px] text-on-surface">
                  {u.full_name || u.email}
                </span>
                <span className="truncate text-[12px] text-on-surface-variant">{u.email}</span>
              </label>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              disabled={chosen.size === 0 || add.isPending}
              onClick={() => add.mutate()}
            >
              {add.isPending
                ? t('common.saving')
                : t('adminMarathonDetail.addChosen', { count: chosen.size })}
            </Button>
            {/* ⚠️ ТОЛЬКО admin: маршрут `/admin/imports/new` под RequireAdmin, и
                manager уехал бы редиректом на `/admin`. Так же спрятаны два
                других входа — кнопка в шапке «Импортов» и ссылка в «Добавить
                группу». Курс уходит в адресе: на экране импорта он уже выбран,
                выбирать его второй раз (и ошибиться) не нужно. */}
            {role === 'admin' && (
              <Link to={`/admin/imports/new?marathon_id=${encodeURIComponent(marathonId)}`}>
                <Button variant="ghost">
                  <span className="material-symbols-outlined text-[18px]">upload_file</span>
                  {t('adminStudentImport.open')}
                </Button>
              </Link>
            )}
          </div>

          <div className="mt-5 border-t border-outline-variant pt-4">
            <CardTitle>{t('adminMarathonDetail.newStudentTitle')}</CardTitle>
            <p className="mb-3 mt-1 text-[13px] text-on-surface-variant">
              {t('adminMarathonDetail.newStudentMailNote')}
            </p>
            <div className="grid gap-2.5 sm:grid-cols-2">
              <Input
                label={t('adminMarathonDetail.newStudentName')}
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                  setNewNote('');
                  setJustAdded(null);
                }}
              />
              <Input
                type="email"
                label={t('adminMarathonDetail.newStudentEmail')}
                value={newEmail}
                onChange={(e) => {
                  setNewEmail(e.target.value);
                  setNewNote('');
                  setJustAdded(null);
                }}
              />
            </div>
            {newNote && <p className="mt-2 text-[13px] text-primary">{newNote}</p>}
            {justAdded && (
              <div className="mt-2 flex justify-start">
                <AccessCopyButton userId={justAdded.id} email={justAdded.email} />
              </div>
            )}
            <Button
              className="mt-3"
              disabled={!newName.trim() || !newEmail.trim() || createNew.isPending}
              onClick={() => createNew.mutate()}
            >
              {createNew.isPending
                ? t('common.saving')
                : t('adminMarathonDetail.newStudentCreate')}
            </Button>
          </div>
        </Card>
      )}
    </>
  );

  if (rows.length === 0)
    return (
      <div>
        {addBlock}
        <Card variant="subtle" className="text-center text-on-surface-variant">
          {t('adminMarathonDetail.noStudents')}
        </Card>
      </div>
    );

  // Поиск — по уже загруженному списку: вкладка и так получает всех учеников
  // марафона одним ответом, ходить на сервер за каждой буквой незачем.
  const shown = filterEnrollmentRows(rows, rowQuery);
  const filtering = rowQuery.trim() !== '';

  return (
    <div>
      {addBlock}
      <div data-student-search className="mb-3 w-full">
        <Input
          value={rowQuery}
          onChange={(e) => setRowQuery(e.target.value)}
          placeholder={t('adminMarathonDetail.searchStudent')}
          aria-label={t('adminMarathonDetail.searchStudent')}
          leftIcon={<span className="material-symbols-outlined">search</span>}
        />
      </div>
      <p className="mb-3 text-xs font-semibold uppercase tracking-label text-on-surface-variant">
        {filtering
          ? t('adminMarathonDetail.foundCount', { found: shown.length, total: rows.length })
          : t('adminMarathonDetail.studentsCount', { count: rows.length })}
      </p>
      {notice && (
        <Card
          variant="subtle"
          role="status"
          className={`mb-3 text-sm ${notice.tone === 'error' ? 'text-error' : 'text-on-surface'}`}
        >
          {notice.text}
        </Card>
      )}
      {filtering && shown.length === 0 && (
        <Card variant="subtle" className="text-center text-on-surface-variant">
          {t('adminMarathonDetail.searchEmpty')}
        </Card>
      )}
      <ul className="space-y-2">
        {shown.map((r) => {
          const statusKey = enrollmentStatusKey(r.status);
          return (
            <li key={r.id} data-enrollment={r.id}>
              {/*
                На телефоне две строки: «имя … ⋯» и под ним метки. Сеткой, а не
                flex-wrap: на flex-wrap «⋯» уезжал отдельным нижним ярусом (стенд
                11.09.2026, 390px). «Скопировать данные для входа» живёт в меню «⋯»,
                а не кнопкой в строке — решение владельца 11.09.2026. На компьютере —
                одна строка, «⋯» последним.
              */}
              <Card variant="subtle" className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 sm:flex">
                <div className="flex min-w-0 items-center gap-3 sm:flex-1">
                  <Avatar name={r.student_name} size="sm" />
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-on-surface">{r.student_name || '—'}</span>
                </div>
                <div className="col-start-2 row-start-1 sm:order-last">
                  <StudentRowMenu row={r} marathonId={marathonId} onNotice={setNotice} />
                </div>
                <div className="col-span-2 flex min-w-0 flex-wrap items-center gap-2">
                  {r.curator_name && <Pill variant="neutral">{r.curator_name}</Pill>}
                  <Pill
                    variant={
                      r.status === 'active' ? 'success' : r.status === 'inactive' || r.status === 'pending' ? 'warning' : 'neutral'
                    }
                  >
                    {statusKey ? t(statusKey) : r.status}
                  </Pill>
                </div>
              </Card>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function StatsTab({
  marathonId,
  studentCount,
  lessonCount,
  publishedCount,
}: {
  marathonId: string;
  /** `null` — список учеников ещё грузится. */
  studentCount: number | null;
  lessonCount: number;
  publishedCount: number;
}) {
  const { t } = useTranslation();
  const lb = useQuery({
    queryKey: ['admin', 'marathon-leaderboard', marathonId],
    queryFn: () => progressApi.getLeaderboard(marathonId, 10),
  });
  const top = lb.data ?? [];
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-3">
        <StatCard icon="group" label={t('adminMarathonDetail.statStudents')} value={studentCount ?? '—'} />
        <StatCard icon="menu_book" label={t('adminMarathonDetail.statLessons')} value={lessonCount} />
        <StatCard icon="check_circle" label={t('adminMarathonDetail.statPublished')} value={publishedCount} />
      </div>

      <div>
        <h3 className="mb-2 font-display text-lg font-bold tracking-display text-on-surface">{t('adminMarathonDetail.topStudents')}</h3>
        {top.length === 0 ? (
          <Card variant="subtle" className="text-center text-on-surface-variant">{t('adminMarathonDetail.statsEmpty')}</Card>
        ) : (
          <ul className="space-y-1">
            {top.map((e) => (
              <li key={e.student_id}>
                <Card variant="subtle" className="flex items-center gap-3">
                  <span className="w-6 text-center font-mono text-sm font-bold text-on-surface-variant">{e.rank}</span>
                  <Avatar name={e.student_name} src={e.avatar_url} size="sm" />
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-on-surface">{e.student_name || '—'}</span>
                  <Pill variant="primary">{t('adminMarathonDetail.points', { count: e.total_points })}</Pill>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function LessonEditForm({
  draft,
  onChange,
  buttons,
}: {
  draft: LessonDraft;
  onChange: (next: Partial<LessonDraft>) => void;
  /** «Отмена / Сохранить» под полями; на телефоне — `null`, кнопки в нижней полосе окна. */
  buttons: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <label className="block text-sm">
        {t('adminMarathonDetail.lessonName')}
        <Input value={draft.title} onChange={(e) => onChange({ title: e.target.value })} />
      </label>
      <label className="block text-sm">
        {t('adminMarathonDetail.lessonPointsLabel')}
        <Input
          type="number"
          min={0}
          value={draft.points}
          onChange={(e) => onChange({ points: Number(e.target.value) })}
        />
      </label>
      <label className="block text-sm">
        {t('adminMarathonDetail.lessonStatus')}
        <select
          className="mt-1 w-full rounded-lg border border-outline bg-surface px-3 py-2 text-sm"
          value={draft.status}
          onChange={(e) => onChange({ status: e.target.value as Lesson['status'] })}
        >
          <option value="draft">{t('marathon.statusDraft')}</option>
          <option value="published">{t('marathon.statusPublished')}</option>
        </select>
      </label>
      {buttons}
    </div>
  );
}

// Вкладка тарифов курса (`TariffsTab`, не вызывалась с 07.09.2026) удалена 13.10.2026 вместе с разделом «Тарифы»
// (решение владельца). Тарифы в базе и права по ним не тронуты; тариф ученика — в его карточке.

const UNASSIGNED = '__none__';

export function CuratorsTab({ marathonId, marathonStatus }: { marathonId: string; marathonStatus?: string }) {
  const ask = useConfirm();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkCurator, setBulkCurator] = useState('');
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [rosterQ, setRosterQ] = useState('');

  const enrollments = useQuery({
    queryKey: ['admin', 'marathon', marathonId, 'enrollments'],
    queryFn: () => usersApi.listMarathonEnrollments(marathonId),
  });

  const curators = useQuery({
    queryKey: ['admin', 'curators', 'all'],
    queryFn: () => usersApi.list({ role: 'curator', limit: 200 }),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['admin', 'marathon', marathonId, 'enrollments'] });

  const rows: MarathonEnrollmentRow[] = enrollments.data?.items ?? [];
  const curatorList = curators.data?.items ?? [];
  const enrolledIds = new Set(rows.map((r) => r.student_id));

  // Shared with GroupsPanel via the same query key — used to label each roster
  // row with its group.
  const groupsData = useQuery({
    queryKey: ['admin', 'marathon', marathonId, 'groups'],
    queryFn: () => usersApi.listGroups(marathonId),
  });
  const groupNameById = new Map((groupsData.data?.items ?? []).map((g) => [g.id, g.name] as const));

  const curatorOptions = [
    { value: '', label: t('adminMarathonDetail.selectCurator') },
    ...curatorList.map((c) => ({ value: c.id, label: c.full_name })),
  ];

  // Точечная перепривязка одного ученика.
  const assignOne = useMutation({
    mutationFn: ({ enrollmentId, curatorId }: { enrollmentId: string; curatorId: string }) =>
      usersApi.assignCurator(enrollmentId, curatorId),
    onSuccess: () => { setError(''); invalidate(); },
    // `student_in_group` и прочие отказы этапа 4 — своим текстом: общий
    // `humanizeError` назвал бы любой 409 «запись уже существует».
    onError: (e) => setError(lifecycleErrorText(e, t)),
  });

  // Массовое назначение выбранным (или всем) ученикам — цикл по POST.
  const assignBulk = useMutation({
    mutationFn: async ({ enrollmentIds, curatorId }: { enrollmentIds: string[]; curatorId: string }) => {
      for (const id of enrollmentIds) {
        await usersApi.assignCurator(id, curatorId);
      }
    },
    onSuccess: () => { setError(''); setSelected(new Set()); invalidate(); },
    // Если цикл упал в середине, часть учеников уже переназначена — обязательно
    // перечитываем список, иначе UI показывает устаревших кураторов.
    onError: (e) => { setError(lifecycleErrorText(e, t)); invalidate(); },
  });

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Поиск по составу курса — имя, почта, группа; на клиенте, список приходит целиком (28.09.2026, вторая волна п. 11).
  // «Выбрать всех» — найденных: админ ищет группу и отмечает её разом.
  const shownRows = rows.filter((r) =>
    matchesSearch([r.student_name, r.student_email, r.group_id ? groupNameById.get(r.group_id) : ''], rosterQ),
  );
  const allSelected = shownRows.length > 0 && shownRows.every((r) => selected.has(r.id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const r of shownRows) {
        if (allSelected) next.delete(r.id);
        else next.add(r.id);
      }
      return next;
    });
  };

  /**
   * D25: назначать куратора можно только активному ученику без группы — у
   * ученика в группе кураторы берутся из группы, сервер ответит 409
   * `student_in_group`. Выбор строк остаётся общим: им же пользуется
   * «добавить в группу», и там ученики в группах нужны.
   */
  const pool = selected.size > 0 ? rows.filter((r) => selected.has(r.id)) : rows;
  const assignable = pool.filter(canAssignCurator);
  const statusText = (s: string) => {
    const key = enrollmentStatusKey(s);
    return key ? t(key) : s;
  };

  const runBulk = async () => {
    if (!bulkCurator) { setError(t('adminMarathonDetail.selectCuratorFirst')); return; }
    const targets = assignable;
    if (targets.length === 0) { setError(t('adminMarathonDetail.noStudentsToAssign')); return; }
    // Пустой выбор = действие затрагивает ВСЕХ учеников марафона. Это слишком
    // разрушительно, чтобы делать молча — требуем явного подтверждения.
    if (selected.size === 0 && !await ask({ message: t('adminMarathonDetail.assignAllConfirm', { count: targets.length }) })) {
      return;
    }
    assignBulk.mutate({ enrollmentIds: targets.map((r) => r.id), curatorId: bulkCurator });
  };

  if (enrollments.isLoading) return <Card variant="subtle">{t('common.loading')}</Card>;
  if (enrollments.isError) return <Card variant="subtle" className="text-error">{humanizeError(enrollments.error)}</Card>;

  return (
    <div className="space-y-4">
      {/* Сводка по ученикам и кураторам марафона */}
      <Card variant="subtle">
        <div className="flex items-start justify-between gap-3">
          <CardTitle>{t('adminMarathonDetail.studentsAndCuratorsTitle')}</CardTitle>
          <Button onClick={() => setAddOpen(true)}>
            <span className="material-symbols-outlined">person_add</span>
            {t('adminMarathonDetail.addStudents')}
          </Button>
        </div>
        {(() => {
          const counts = new Map<string, number>();
          for (const r of rows) if (r.curator_id) counts.set(r.curator_id, (counts.get(r.curator_id) ?? 0) + 1);
          // D25: у ученика в группе `curator_id` пуст — его кураторы это кураторы
          // группы. Считать его «без куратора» — неправда. Снятые не в счёт.
          const active = rows.filter((r) => r.status === 'active');
          const covered = active.filter((r) => r.curator_id || r.group_id).length;
          const uncovered = active.length - covered;
          return (
            <div className="mt-2 flex flex-wrap gap-2">
              <Pill variant="neutral">{t('adminMarathonDetail.studentsCount', { count: rows.length })}</Pill>
              <Pill variant={uncovered === 0 ? 'success' : 'warning'}>
                {t('adminMarathonDetail.withCurator', { count: covered })}
              </Pill>
              {uncovered > 0 && (
                <Pill variant="warning">{t('adminMarathonDetail.withoutCurator', { count: uncovered })}</Pill>
              )}
              {[...counts.entries()].map(([cid, n]) => {
                const c = curatorList.find((x) => x.id === cid);
                return (
                  <Pill key={cid} variant="primary">
                    {c?.full_name ?? t('adminMarathonDetail.curatorFallback')}: {n}
                  </Pill>
                );
              })}
            </div>
          );
        })()}
      </Card>

      {/* Группы */}
      <GroupsPanel
        marathonId={marathonId}
        curatorList={curatorList}
        selectedIds={[...selected]}
        onAssigned={() => { setSelected(new Set()); invalidate(); }}
      />

      {/* Массовое назначение (устаревшая точечная привязка куратора — оставлена
          на переходный период; основной путь теперь — группы выше). */}
      <Card>
        <CardTitle>{t('adminMarathonDetail.assignCuratorTitle')}</CardTitle>
        <p className="mt-1 text-xs text-on-surface-variant">
          {selected.size > 0
            ? t('adminMarathonDetail.assignSelectedCount', { count: assignable.length })
            : t('adminMarathonDetail.assignAll')}
        </p>
        {pool.length > assignable.length && (
          <p className="mt-1 text-xs text-on-surface-variant">
            {t('userLifecycle.assignSkipped', { count: pool.length - assignable.length })}
          </p>
        )}
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div className="min-w-[220px] flex-1">
            <select
              data-bulk-curator
              className="h-12 w-full rounded-button bg-surface-container-lowest px-4 shadow-soft"
              value={bulkCurator}
              onChange={(e) => setBulkCurator(e.target.value)}
            >
              {curatorOptions.map((o) => (
                <option key={o.value} value={o.value} disabled={o.value === ''}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <Button data-bulk-assign onClick={runBulk} loading={assignBulk.isPending} disabled={assignable.length === 0}>
            <span className="material-symbols-outlined">group_add</span>
            {selected.size > 0 ? t('adminMarathonDetail.assignSelectedBtn', { count: selected.size }) : t('adminMarathonDetail.assignToMarathon')}
          </Button>
        </div>
        {error && <p className="mt-2 text-sm text-error">{error}</p>}
      </Card>

      {/* Список учеников */}
      {rows.length === 0 ? (
        <Card variant="subtle" className="text-center text-on-surface-variant">
          {t('adminMarathonDetail.noEnrollments')}
        </Card>
      ) : (
        <Card>
          <SearchInput
            className="mb-2"
            value={rosterQ}
            placeholder={t('adminMarathonDetail.rosterSearch')}
            onSearch={setRosterQ}
          />
          <p className="mb-2 text-xs text-on-surface-variant">
            {t('common.shownOf', { shown: shownRows.length, total: rows.length })}
          </p>
          <div className="mb-2 flex items-center gap-2 border-b border-outline pb-2">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4 accent-primary" />
            <span className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
              {t('adminMarathonDetail.selectAll')}
            </span>
          </div>
          <ul className="max-h-[40rem] space-y-2 overflow-y-auto pr-1">
            {shownRows.map((r) => (
              <li key={r.id} data-enrollment={r.id}>
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selected.has(r.id)}
                    onChange={() => toggle(r.id)}
                    className="h-4 w-4 accent-primary"
                  />
                  <Avatar name={r.student_name} size="sm" />
                  <div className="min-w-[140px] flex-1">
                    <p className="text-sm font-semibold">{r.student_name || t('adminMarathonDetail.noName')}</p>
                    {/* У ученика в группе куратор — это кураторы группы, а не пустое поле. */}
                    {(r.curator_name || !r.group_id) && (
                      <p className="text-xs text-on-surface-variant">
                        {r.curator_name ? t('adminMarathonDetail.curatorAssigned', { name: r.curator_name }) : t('adminMarathonDetail.noCurator')}
                      </p>
                    )}
                    {r.group_id && groupNameById.get(r.group_id) && (
                      <span className="mt-1 inline-flex items-center gap-1 rounded-pill bg-secondary-container px-2 py-0.5 text-[11px] font-medium text-on-secondary-container">
                        <span className="material-symbols-outlined text-xs">groups</span>
                        {groupNameById.get(r.group_id)}
                      </span>
                    )}
                  </div>
                  {canAssignCurator(r) ? (
                    <select
                      className="h-10 min-w-[180px] rounded-button bg-surface-container-lowest px-3 text-sm shadow-soft"
                      value={r.curator_id ?? UNASSIGNED}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === UNASSIGNED || v === r.curator_id) return;
                        assignOne.mutate({ enrollmentId: r.id, curatorId: v });
                      }}
                    >
                      <option value={UNASSIGNED} disabled>
                        {t('adminMarathonDetail.selectCurator')}
                      </option>
                      {curatorList.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.full_name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-xs text-on-surface-variant">
                      {r.status === 'active' ? t('userLifecycle.curatorViaGroup') : statusText(r.status)}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <AddStudentsModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        marathonId={marathonId}
        enrolledIds={enrolledIds}
        onEnrolled={invalidate}
        marathonStatus={marathonStatus}
        groupCount={(groupsData.data?.items ?? []).length}
      />
    </div>
  );
}

/**
 * Modal for enrolling multiple students into a marathon at once.
 * Lists platform students (excluding already-enrolled), lets the admin pick
 * many, then calls the bulk-enroll endpoint and reports created/skipped counts.
 */
function AddStudentsModal({
  open,
  onClose,
  marathonId,
  enrolledIds,
  onEnrolled,
  marathonStatus,
  groupCount,
}: {
  open: boolean;
  onClose: () => void;
  marathonId: string;
  enrolledIds: Set<string>;
  onEnrolled: () => void;
  /** Статус курса и число групп — предупреждения перед заливкой (техдолг 423). */
  marathonStatus?: string;
  groupCount?: number;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ created: number; skipped: number; failed: number; partial: boolean } | null>(null);

  const students = useQuery({
    queryKey: ['admin', 'students', 'for-enroll', search],
    queryFn: () => usersApi.list({ role: 'student', status: 'active', q: search, limit: 200 }),
    enabled: open,
  });

  // Кандидаты — активные ученики, ещё не зачисленные в этот марафон.
  const candidates = (students.data?.items ?? []).filter((s) => !enrolledIds.has(s.id));

  const enroll = useMutation({
    mutationFn: () =>
      usersApi.bulkEnroll({ marathon_id: marathonId, student_ids: [...picked] }),
    onSuccess: (res) => {
      setResult({
        created: res.created?.length ?? 0,
        skipped: res.skipped?.length ?? 0,
        failed: res.failed ?? 0,
        partial: res.partial ?? false,
      });
      setPicked(new Set());
      onEnrolled();
    },
    onError: (e) => setError(humanizeError(e)),
  });

  const toggle = (id: string) => {
    setResult(null);
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const close = () => {
    setPicked(new Set());
    setSearch('');
    setError('');
    setResult(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={close} title={t('adminMarathonDetail.addStudentsTitle')} size="lg">
      <div className="space-y-3">
        {/* Кандидаты ищутся на сервере — с задержкой, а не запросом на каждую букву (28.09.2026). */}
        <SearchInput placeholder={t('adminMarathonDetail.searchStudentsPlaceholder')} value={search} onSearch={setSearch} />

        {/* Заливка в черновик / в курс без групп — предупреждение до подтверждения (техдолг 423). */}
        {(marathonStatus !== undefined && marathonStatus !== 'published') || groupCount === 0 ? (
          <Card variant="subtle" className="text-sm text-warning">
            {marathonStatus !== undefined && marathonStatus !== 'published' && (
              <p>{t('adminMarathonDetail.enrollDraftWarning')}</p>
            )}
            {groupCount === 0 && <p>{t('adminMarathonDetail.enrollNoGroupsWarning')}</p>}
          </Card>
        ) : null}

        {students.isLoading ? (
          <Card variant="subtle">{t('common.loading')}</Card>
        ) : candidates.length === 0 ? (
          <Card variant="subtle" className="text-center text-on-surface-variant">
            {t('adminMarathonDetail.noStudentsToAdd')}
          </Card>
        ) : (
          <ul className="max-h-72 space-y-1 overflow-y-auto">
            {candidates.map((s) => (
              <li key={s.id}>
                <label className="flex cursor-pointer items-center gap-3 rounded-button px-2 py-2 hover:bg-surface-container-low">
                  <input
                    type="checkbox"
                    checked={picked.has(s.id)}
                    onChange={() => toggle(s.id)}
                    className="h-4 w-4 accent-primary"
                  />
                  <Avatar name={s.full_name} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{s.full_name || t('adminMarathonDetail.noName')}</p>
                    <p className="truncate text-xs text-on-surface-variant">{s.email}</p>
                  </div>
                </label>
              </li>
            ))}
          </ul>
        )}

        {result && (
          <div className="text-sm">
            <p className="text-on-surface">
              {t('adminMarathonDetail.enrollResult', { created: result.created, skipped: result.skipped })}
            </p>
            {result.failed > 0 && (
              <p className="text-error">{t('adminMarathonDetail.enrollFailed', { failed: result.failed })}</p>
            )}
            {result.partial && (
              <p className="text-warning">{t('adminMarathonDetail.enrollPartial')}</p>
            )}
          </div>
        )}
        {error && <p className="text-sm text-error">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={close}>{t('common.cancel')}</Button>
          <Button
            onClick={() => { setError(''); enroll.mutate(); }}
            loading={enroll.isPending}
            disabled={picked.size === 0}
          >
            <span className="material-symbols-outlined">person_add</span>
            {t('adminMarathonDetail.enrollSelected', { count: picked.size })}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
